import { jsx } from 'react/jsx-runtime';
import MUIButton from '@mui/material/Button';
import MUITextField from '@mui/material/TextField';
import MUIIcon from '@mui/material/Icon';
import MUICheckbox from '@mui/material/Checkbox';
import MUISlider from '@mui/material/Slider';

const Button = (props) => jsx(MUIButton, { ...props });

/** Render any incoming value as the string the input element displays. */
const toDisplayString = (value) => (value == null ? '' : String(value));
/**
 * Parse the raw string typed into a numeric input into a real JSON number.
 * Returns `null` for empty/incomplete input so the store never holds `NaN`.
 */
const parseNumber = (raw) => {
    if (raw.trim() === '')
        return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
};
/**
 * Hookless helper for store-bound text/number inputs. Returns props for an
 * uncontrolled input so the DOM owns the text (caret stays put, slow stores
 * can't clobber keystrokes); the store value is pushed back into the field only
 * while it is not focused. Numeric inputs emit a JSON number (or null when empty).
 *
 * Attach `ref` to the real `<input>` element — for MUI's TextField pass it as `inputRef`.
 */
const uncontrolledInputProps = (value, onChange, type) => {
    const display = toDisplayString(value);
    const ref = (el) => {
        if (el == null)
            return;
        if (typeof document !== 'undefined' && document.activeElement === el)
            return;
        if (el.value !== display)
            el.value = display;
    };
    const handleChange = (e) => {
        if (type === 'number') {
            const numeric = parseNumber(e.target.value);
            const syntheticEvent = {
                ...e,
                target: { ...e.target, value: numeric },
                currentTarget: e.currentTarget,
            };
            onChange?.(syntheticEvent);
            return;
        }
        onChange?.(e);
    };
    return { defaultValue: display, onChange: handleChange, ref };
};

const TextField = ({ value, onChange, select, type, ...props }) => {
    if (select) {
        return (jsx(MUITextField, { ...props, type: type, select: true, value: value ?? '', onChange: onChange }));
    }
    const { defaultValue, onChange: inputOnChange, ref: inputRef } = uncontrolledInputProps(value, onChange, type);
    return jsx(MUITextField, { ...props, type: type, defaultValue: defaultValue, onChange: inputOnChange, inputRef: inputRef });
};

const Icon = (props) => {
    const type = ['FontAwesome', 'MaterialIcons'].includes(props.type) ? props.type : 'MaterialIcons';
    return (jsx(MUIIcon, { ...props, className: type === 'FontAwesome' ? `fa fa-${props.name}` : undefined, children: type === 'FontAwesome' ? undefined : props.name }));
};

const Checkbox = ({ onChange, ...props }) => {
    return jsx(MUICheckbox, { ...props, onChange: onChange });
};

const Slider = (props) => {
    const changeHandler = props.onChange;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return jsx(MUISlider, { ...props, onChange: changeHandler });
};

export { Button, Checkbox, Icon, Slider, TextField };
//# sourceMappingURL=index.js.map
